import { CHOICE_TYPES, type FormDefinition, type Question, type Section } from '../../lib/types';
import { newId } from '../../lib/utils';

export interface TemplateIssue {
  sectionId?: string;
  questionId?: string;
  message: string;
}

/** Trims choice options and drops blank or repeated ones, as the server would reject them. */
export function cleanDraft(draft: FormDefinition): FormDefinition {
  return {
    ...draft,
    sections: draft.sections.map((s) => ({
      ...s,
      questions: s.questions.map((q) =>
        q.options
          ? { ...q, options: [...new Set(q.options.map((o) => o.trim()).filter(Boolean))] }
          : q,
      ),
    })),
  };
}

/** Problems that would stop the template from saving, in the order they appear. */
export function findIssues(draft: FormDefinition): TemplateIssue[] {
  const issues: TemplateIssue[] = [];
  if (!draft.name.trim()) issues.push({ message: 'The form needs a title' });
  for (const s of draft.sections) {
    if (!s.title.trim()) issues.push({ sectionId: s.id, message: 'Section title is required' });
    for (const q of s.questions) {
      const problem = questionProblem(q);
      if (problem) issues.push({ sectionId: s.id, questionId: q.id, message: problem });
    }
  }
  return issues;
}

function questionProblem(q: Question): string | undefined {
  if (!q.label.trim()) return 'Question text is required';
  if (CHOICE_TYPES.has(q.type) && !q.options?.some((o) => o.trim()))
    return 'Add at least one option';
  return undefined;
}

export function countRequired(sections: readonly Section[]): number {
  return sections.reduce((n, s) => n + s.questions.filter((q) => q.required).length, 0);
}

/** Deep copy of a section with fresh ids, so it can live next to the original. */
export function copySection(source: Section): Section {
  return {
    ...source,
    id: newId('s'),
    title: `${source.title} (copy)`,
    questions: source.questions.map((q) => ({
      ...q,
      id: newId('q'),
      options: q.options && [...q.options],
    })),
  };
}
